/**
 * PerformanceDashboard — API Performance Monitoring
 * Tracks response times, router health, endpoint hit counts, errors.
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Activity, Zap, AlertTriangle, Clock, Server, ArrowUpRight,
  ArrowDownRight, RefreshCw, Shield, TrendingUp, Hash, XCircle,
  Lightbulb, ChevronRight, HeartPulse, RotateCcw, CheckCircle2
} from 'lucide-react';

// ─── Uptime formatter ───────────────────────────────────────────────────
function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ─── Summary Cards ──────────────────────────────────────────────────────
function SummaryCards({ summary }) {
  if (!summary) return null;
  const cards = [
    {
      label: 'Total Requests',
      value: summary.total_requests.toLocaleString(),
      icon: Hash,
      color: 'bg-stone-900 text-white',
      iconColor: 'text-stone-400',
    },
    {
      label: 'Avg Response',
      value: `${summary.avg_response_time_ms.toFixed(0)}ms`,
      icon: Zap,
      color: summary.avg_response_time_ms > 500 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200',
      iconColor: summary.avg_response_time_ms > 500 ? 'text-amber-600' : 'text-green-600',
      textColor: summary.avg_response_time_ms > 500 ? 'text-amber-900' : 'text-green-900',
    },
    {
      label: 'Error Rate',
      value: `${summary.error_rate}%`,
      icon: AlertTriangle,
      color: summary.error_rate > 5 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200',
      iconColor: summary.error_rate > 5 ? 'text-red-600' : 'text-green-600',
      textColor: summary.error_rate > 5 ? 'text-red-900' : 'text-green-900',
    },
    {
      label: 'Uptime',
      value: formatUptime(summary.uptime_seconds),
      icon: Clock,
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      textColor: 'text-blue-900',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} className={`rounded-lg p-4 border ${c.color}`} data-testid={`summary-card-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-medium ${c.color.includes('stone-900') ? 'text-stone-400' : 'text-stone-500'}`}>{c.label}</p>
              <Icon size={16} className={c.iconColor} />
            </div>
            <p className={`text-2xl font-bold ${c.textColor || (c.color.includes('stone-900') ? 'text-white' : 'text-stone-900')}`}>
              {c.value}
            </p>
            <p className={`text-xs mt-1 ${c.color.includes('stone-900') ? 'text-stone-500' : 'text-stone-400'}`}>
              {i === 0 ? `${summary.unique_endpoints} endpoints` : 
               i === 1 ? 'across all endpoints' :
               i === 2 ? `${summary.total_errors} total errors` :
               'since last restart'}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ─── Timeline Chart ─────────────────────────────────────────────────────
function TimelineChart({ timeline }) {
  if (!timeline || timeline.length === 0) return null;

  const maxReq = Math.max(...timeline.map(t => t.requests), 1);
  const maxTime = Math.max(...timeline.map(t => t.avg_time_ms), 1);
  // Show last 30 data points with at least one request
  const relevantData = timeline.filter(t => t.requests > 0).slice(-30);
  
  if (relevantData.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="timeline-chart">
        <h3 className="font-semibold text-stone-800 text-sm mb-4 flex items-center gap-2">
          <TrendingUp size={14} /> Request Timeline
        </h3>
        <div className="text-center text-stone-400 py-8 text-sm">No traffic data yet. Use the app to generate metrics.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="timeline-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-stone-800 text-sm flex items-center gap-2">
          <TrendingUp size={14} /> Request Timeline (Last Hour)
        </h3>
        <div className="flex items-center gap-4 text-xs text-stone-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-stone-800 inline-block" /> Requests</span>
          <span className="flex items-center gap-1"><span className="w-3 h-1.5 rounded bg-blue-500 inline-block" /> Avg ms</span>
        </div>
      </div>
      <div className="flex items-end gap-1 h-36">
        {relevantData.map((t, i) => {
          const barH = Math.max(4, (t.requests / maxReq) * 100);
          const timeH = t.avg_time_ms > 0 ? Math.max(2, (t.avg_time_ms / maxTime) * 100) : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
              <div className="w-full flex flex-col items-center gap-0.5" style={{ height: '100%', justifyContent: 'flex-end' }}>
                <div
                  className="w-full rounded-t bg-stone-800 transition-all hover:bg-stone-700 min-w-[4px]"
                  style={{ height: `${barH}%` }}
                  title={`${t.time.slice(11)}: ${t.requests} req, ${t.avg_time_ms.toFixed(0)}ms avg${t.errors > 0 ? `, ${t.errors} errors` : ''}`}
                />
              </div>
              {t.errors > 0 && <div className="w-1.5 h-1.5 rounded-full bg-red-500 absolute -top-1" />}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-[10px] text-stone-400">
        <span>{relevantData[0]?.time?.slice(11, 16)}</span>
        <span>{relevantData[relevantData.length - 1]?.time?.slice(11, 16)}</span>
      </div>
    </div>
  );
}

// ─── Router Health Grid ─────────────────────────────────────────────────
function RouterHealthGrid({ routers }) {
  if (!routers || routers.length === 0) return null;

  const statusColor = {
    healthy: 'bg-green-100 text-green-800 border-green-200',
    degraded: 'bg-amber-100 text-amber-800 border-amber-200',
    unhealthy: 'bg-red-100 text-red-800 border-red-200',
  };
  const statusDot = {
    healthy: 'bg-green-500',
    degraded: 'bg-amber-500',
    unhealthy: 'bg-red-500',
  };

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="router-health">
      <h3 className="font-semibold text-stone-800 text-sm mb-4 flex items-center gap-2">
        <Server size={14} /> Router Health ({routers.length} active)
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {routers.map((r, i) => (
          <div
            key={i}
            className={`rounded-lg border p-3 ${statusColor[r.status] || statusColor.healthy}`}
            data-testid={`router-card-${i}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2 h-2 rounded-full ${statusDot[r.status] || statusDot.healthy}`} />
              <span className="text-xs font-semibold truncate">{r.router}</span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="opacity-70">Hits</span>
                <span className="font-medium">{r.request_count.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-70">Avg</span>
                <span className="font-medium">{r.avg_time_ms.toFixed(0)}ms</span>
              </div>
              {r.error_count > 0 && (
                <div className="flex justify-between">
                  <span className="opacity-70">Errors</span>
                  <span className="font-medium">{r.error_count} ({r.error_rate}%)</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Top Endpoints Table ────────────────────────────────────────────────
function EndpointsTable({ endpoints }) {
  if (!endpoints || endpoints.length === 0) return null;

  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="endpoints-table">
      <div className="px-5 py-3 border-b border-stone-200">
        <h3 className="font-semibold text-stone-800 text-sm flex items-center gap-2">
          <Activity size={14} /> Top Endpoints by Hit Count
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-600 text-xs">
            <tr>
              <th className="p-3 text-left">Endpoint</th>
              <th className="p-3 text-right">Hits</th>
              <th className="p-3 text-right">Avg (ms)</th>
              <th className="p-3 text-right">P95 (ms)</th>
              <th className="p-3 text-right">Max (ms)</th>
              <th className="p-3 text-right">Errors</th>
              <th className="p-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((ep, i) => {
              const isHot = ep.avg_time_ms > 500;
              const hasErrors = ep.error_rate > 0;
              return (
                <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`endpoint-row-${i}`}>
                  <td className="p-3">
                    <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded font-mono text-stone-700">
                      {ep.endpoint}
                    </code>
                  </td>
                  <td className="p-3 text-right font-semibold text-stone-800">{ep.count.toLocaleString()}</td>
                  <td className={`p-3 text-right font-medium ${isHot ? 'text-amber-700' : 'text-stone-700'}`}>
                    {ep.avg_time_ms.toFixed(0)}
                  </td>
                  <td className="p-3 text-right text-stone-600">{ep.p95_time_ms.toFixed(0)}</td>
                  <td className="p-3 text-right text-stone-500">{ep.max_time_ms.toFixed(0)}</td>
                  <td className={`p-3 text-right font-medium ${hasErrors ? 'text-red-600' : 'text-stone-400'}`}>
                    {ep.error_count > 0 ? `${ep.error_count} (${ep.error_rate}%)` : '-'}
                  </td>
                  <td className="p-3 text-center">
                    {isHot ? (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Slow</Badge>
                    ) : hasErrors ? (
                      <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-[10px]">Errors</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">OK</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Recent Errors ──────────────────────────────────────────────────────
function RecentErrors({ errors }) {
  if (!errors || errors.length === 0) return (
    <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="recent-errors">
      <h3 className="font-semibold text-stone-800 text-sm mb-4 flex items-center gap-2">
        <XCircle size={14} /> Recent Errors
      </h3>
      <div className="text-center py-6 text-sm text-stone-400">
        <Shield size={28} className="mx-auto mb-2 text-green-400" />
        No errors detected. All systems operational.
      </div>
    </div>
  );

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="recent-errors">
      <h3 className="font-semibold text-stone-800 text-sm mb-4 flex items-center gap-2">
        <XCircle size={14} className="text-red-500" /> Recent Errors ({errors.length})
      </h3>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {errors.map((err, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b border-stone-100 last:border-0" data-testid={`error-row-${i}`}>
            <Badge variant="outline" className={`text-[10px] min-w-[40px] text-center ${
              err.status_code >= 500 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}>{err.status_code}</Badge>
            <code className="text-xs text-stone-600 font-mono flex-1 truncate">{err.method} {err.path}</code>
            <span className="text-xs text-stone-400 flex-shrink-0">{err.response_time_ms?.toFixed(0)}ms</span>
            <span className="text-[10px] text-stone-400 flex-shrink-0">{err.timestamp?.slice(11, 19)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Diagnostics & Recommendations ──────────────────────────────────────
function DiagnosticsPanel({ diagnostics, healthScore, onReset }) {
  if (!diagnostics) return null;

  const severityConfig = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, iconColor: 'text-red-600', label: 'CRITICAL', labelBg: 'bg-red-600 text-white' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, iconColor: 'text-amber-600', label: 'WARNING', labelBg: 'bg-amber-500 text-white' },
    info: { bg: 'bg-green-50', border: 'border-green-200', icon: CheckCircle2, iconColor: 'text-green-600', label: 'HEALTHY', labelBg: 'bg-green-600 text-white' },
  };

  const scoreColor = healthScore >= 80 ? 'text-green-600' : healthScore >= 50 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = healthScore >= 80 ? 'bg-green-50 border-green-200' : healthScore >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

  return (
    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="diagnostics-panel">
      <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HeartPulse size={18} className="text-stone-700" />
          <h3 className="font-semibold text-stone-800 text-sm">Diagnostics & Recommendations</h3>
          <div className={`ml-2 px-3 py-1 rounded-full border text-sm font-bold ${scoreBg} ${scoreColor}`} data-testid="health-score">
            {healthScore}/100
          </div>
        </div>
        <button
          onClick={onReset}
          className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-red-50"
          data-testid="reset-metrics-btn"
          title="Reset all metrics"
        >
          <RotateCcw size={12} /> Reset Metrics
        </button>
      </div>
      <div className="p-5 space-y-3">
        {diagnostics.length === 0 ? (
          <div className="text-center py-8 text-stone-400">
            <Shield size={32} className="mx-auto mb-2 text-green-400" />
            <p className="text-sm">No issues detected. All systems running smoothly.</p>
          </div>
        ) : (
          diagnostics.map((d, idx) => {
            const config = severityConfig[d.severity] || severityConfig.info;
            const Icon = config.icon;
            return (
              <div key={d.id || idx} className={`rounded-lg border ${config.border} ${config.bg} p-4`} data-testid={`diagnostic-${idx}`}>
                <div className="flex items-start gap-3">
                  <Icon size={18} className={`${config.iconColor} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${config.labelBg}`}>{config.label}</span>
                      <h4 className="text-sm font-semibold text-stone-900">{d.title}</h4>
                      {d.metric && <span className="text-xs font-mono text-stone-500 ml-auto">{d.metric}</span>}
                    </div>
                    <p className="text-xs text-stone-600 mt-1">{d.description}</p>
                    <div className="mt-2 flex items-start gap-1.5 bg-white/60 rounded p-2.5">
                      <Lightbulb size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-stone-700 font-medium">{d.recommendation}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────
export default function PerformanceDashboard() {
  const token = localStorage.getItem('token');
  const [summary, setSummary] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [routers, setRouters] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [errors, setErrors] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [healthScore, setHealthScore] = useState(100);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [sumRes, epRes, rtRes, tlRes, errRes, diagRes] = await Promise.all([
        axios.get(`${API}/admin/performance/summary`, { headers }),
        axios.get(`${API}/admin/performance/endpoints?limit=20`, { headers }),
        axios.get(`${API}/admin/performance/routers`, { headers }),
        axios.get(`${API}/admin/performance/timeline?minutes=60`, { headers }),
        axios.get(`${API}/admin/performance/errors?limit=15`, { headers }),
        axios.get(`${API}/admin/performance/diagnostics`, { headers }),
      ]);
      setSummary(sumRes.data);
      setEndpoints(epRes.data);
      setRouters(rtRes.data);
      setTimeline(tlRes.data);
      setErrors(errRes.data);
      setDiagnostics(diagRes.data?.diagnostics || []);
      setHealthScore(diagRes.data?.health_score ?? 100);
    } catch (e) {
      if (!silent) toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleResetMetrics = async () => {
    if (!window.confirm('Are you sure you want to reset all performance metrics? This cannot be undone.')) return;
    try {
      await axios.post(`${API}/admin/performance/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Performance metrics reset successfully');
      fetchAll();
    } catch (e) {
      toast.error('Failed to reset metrics');
    }
  };

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchAll(true), 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAll]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-120px)]">
          <div className="text-center text-stone-400">
            <Activity size={36} className="mx-auto mb-3 animate-pulse" />
            <p className="text-sm">Loading performance metrics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="performance-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-900" data-testid="perf-title">Performance Dashboard</h1>
            <p className="text-sm text-stone-500 mt-1">Real-time API performance monitoring and health</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                autoRefresh ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
              data-testid="auto-refresh-toggle"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-stone-400'}`} />
              {autoRefresh ? 'Live' : 'Paused'}
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAll(true)}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <SummaryCards summary={summary} />
        <DiagnosticsPanel diagnostics={diagnostics} healthScore={healthScore} onReset={handleResetMetrics} />
        <TimelineChart timeline={timeline} />
        <RouterHealthGrid routers={routers} />
        <EndpointsTable endpoints={endpoints} />
        <RecentErrors errors={errors} />
      </div>
    </Layout>
  );
}
